import {
  useState,
  useEffect,
  useRef,
  createContext,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "@/services/firebase/config";
import { apiClient } from "@/services/api/base.service";
import {
  identifyAnalyticsUser,
  trackLogin,
  trackSignUp,
  trackStartupCreated,
} from "@/services/firebase/analytics.service";
import {
  authService,
  type SignUpData,
  type SignInData,
} from "@/services/firebase/auth.service";
import type { User } from "@/types/startup.types";

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  profileLoading: boolean;
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (data: SignInData) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signUpWithGoogle: (startupData?: {
    startupName?: string;
    initialFunding?: number;
  }) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  showStartupOnboarding: boolean;
  createStartupForNewUser: (startupData: {
    startupName: string;
    initialFunding?: number;
    industry?: string;
    description?: string;
  }) => Promise<void>;
  skipStartupSetup: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showStartupOnboarding, setShowStartupOnboarding] = useState(false);
  const [, setPendingNewUser] = useState<FirebaseUser | null>(null);

  // Use ref to track current userProfile to avoid unnecessary API calls
  const userProfileRef = useRef<User | null>(null);

  // Update ref when userProfile changes
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount

    // Timeout fallback - if auth doesn't initialize in 5 seconds, show content anyway
    const timeoutId = setTimeout(() => {
      console.log("Auth timeout reached, setting loading to false");
      if (isMounted) setLoading(false);
    }, 5000);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        console.log("Auth state changed:", user?.email || "No user");
        clearTimeout(timeoutId);
        if (!isMounted) return;

        setFirebaseUser(user);
        void identifyAnalyticsUser(user?.uid ?? null);

        if (user) {
          setProfileLoading(true);
          // Only fetch profile if we don't already have one for this user
          if (
            !userProfileRef.current ||
            userProfileRef.current.id !== user.uid
          ) {
            try {
              const profile = await authService.getUserProfile();
              if (isMounted && profile) {
                userProfileRef.current = profile;
                setUserProfile(profile);
              } else if (isMounted && !profile) {
                // In development, create a minimal profile if API is not available
                if (import.meta.env.MODE === "development") {
                  const fallbackProfile: User = {
                    id: user.uid,
                    email: user.email || "",
                    displayName: user.displayName || "Dev User",
                    photoURL: user.photoURL || undefined,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  };
                  userProfileRef.current = fallbackProfile;
                  setUserProfile(fallbackProfile);
                } else if (!userProfileRef.current) {
                  setUserProfile(null);
                }
              }
            } catch (error) {
              console.log("Profile fetch failed:", error);

              // In development, create a minimal profile if API is not available
              if (
                import.meta.env.MODE === "development" &&
                error instanceof TypeError
              ) {
                const fallbackProfile: User = {
                  id: user.uid,
                  email: user.email || "",
                  displayName: user.displayName || "Dev User",
                  photoURL: user.photoURL || undefined,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                };
                if (isMounted) setUserProfile(fallbackProfile);
              } else if (isMounted && !userProfileRef.current) {
                setUserProfile(null);
              }
            } finally {
              if (isMounted) setProfileLoading(false);
            }
          } else {
            // We already have the profile for this user
            if (isMounted) setProfileLoading(false);
          }
        } else {
          if (isMounted) {
            setUserProfile(null);
            setShowStartupOnboarding(false);
            setPendingNewUser(null);
            setProfileLoading(false);
          }
        }

        if (isMounted) setLoading(false);
      },
      (error) => {
        console.error("Auth state error:", error);
        clearTimeout(timeoutId);
        if (isMounted) setLoading(false);
      },
    );

    return () => {
      isMounted = false;
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []); // Keep empty dependency array to avoid infinite loops

  const signUp = async (data: SignUpData) => {
    setLoading(true);
    try {
      const { user, profile } = await authService.signUp(data);
      setFirebaseUser(user);
      setUserProfile(profile);
      void trackSignUp("email");
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (data: SignInData) => {
    setLoading(true);
    try {
      const user = await authService.signIn(data);
      setFirebaseUser(user);
      void trackLogin("email");
      // Profile will be loaded by onAuthStateChanged
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const { user, isNewUser, profile } = await authService.signInWithGoogle();
      setFirebaseUser(user);

      if (isNewUser) {
        void trackSignUp("google");
        setPendingNewUser(user);
        setShowStartupOnboarding(true);
      } else {
        void trackLogin("google");
        if (profile) {
          userProfileRef.current = profile;
          setUserProfile(profile);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const signUpWithGoogle = async (startupData?: {
    startupName?: string;
    initialFunding?: number;
  }) => {
    setLoading(true);
    try {
      const { user, profile } = await authService.signUpWithGoogle(startupData);
      setFirebaseUser(user);
      setUserProfile(profile);
      void trackSignUp("google");
    } finally {
      setLoading(false);
    }
  };

  const createStartupForNewUser = async (startupData: {
    startupName: string;
    initialFunding?: number;
    industry?: string;
    description?: string;
  }) => {
    setLoading(true);
    try {
      await authService.createStartup(startupData);
      void trackStartupCreated("onboarding");
      setShowStartupOnboarding(false);
      setPendingNewUser(null);

      await queryClient.invalidateQueries({ queryKey: ["financial", "startups"] });

      const profile = await authService.getUserProfile();
      setUserProfile(profile);
    } finally {
      setLoading(false);
    }
  };

  const skipStartupSetup = async () => {
    setLoading(true);
    try {
      // Create basic user profile without startup
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        await apiClient.post<User>(
          "/auth/profile",
          {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || "",
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        // Refresh profile
        const profile = await authService.getUserProfile();
        setUserProfile(profile);
      }
    } catch (error) {
      console.error("Error creating basic profile:", error);
    } finally {
      setShowStartupOnboarding(false);
      setPendingNewUser(null);
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await authService.signOut();
      setFirebaseUser(null);
      setUserProfile(null);
      void identifyAnalyticsUser(null);
      setShowStartupOnboarding(false);
      setPendingNewUser(null);
      
      // Clear React Query cache to prevent data leakage between users
      queryClient.clear();
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    return authService.resetPassword(email);
  };

  const value = {
    firebaseUser,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signUpWithGoogle,
    signOut,
    resetPassword,
    showStartupOnboarding,
    createStartupForNewUser,
    skipStartupSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
